import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import CommentForm from './CommentForm';

const formatNorwegianDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const months = ['januar','februar','mars','april','mai','juni','juli','august','september','oktober','november','desember'];
  return `${date?.getDate()}. ${months?.[date?.getMonth()]} ${date?.getFullYear()}, kl. ${String(date?.getHours())?.padStart(2,'0')}:${String(date?.getMinutes())?.padStart(2,'0')}`;
};

const CommentItem = ({ comment, depth = 0, onReplySuccess, isAdmin, onModerate }) => {
  const [showReply, setShowReply] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(comment?.likes_count || 0);

  const sessionId = (() => {
    let sid = sessionStorage.getItem('np_sid');
    if (!sid) { sid = Math.random()?.toString(36)?.slice(2); sessionStorage.setItem('np_sid', sid); }
    return sid;
  })();

  useEffect(() => {
    const checkLiked = async () => {
      const { data } = await supabase?.from('comment_likes')?.select('id')?.eq('comment_id', comment?.id)?.eq('session_id', sessionId)?.maybeSingle();
      setLiked(!!data);
    };
    checkLiked();
  }, [comment?.id]);

  const handleLike = async () => {
    if (liked) {
      await supabase?.from('comment_likes')?.delete()?.eq('comment_id', comment?.id)?.eq('session_id', sessionId);
      await supabase?.from('comments')?.update({ likes_count: Math.max(0, likesCount - 1) })?.eq('id', comment?.id);
      setLikesCount(prev => Math.max(0, prev - 1));
      setLiked(false);
    } else {
      await supabase?.from('comment_likes')?.insert({ comment_id: comment?.id, session_id: sessionId });
      await supabase?.from('comments')?.update({ likes_count: likesCount + 1 })?.eq('id', comment?.id);
      setLikesCount(prev => prev + 1);
      setLiked(true);
    }
  };

  const statusColors = { approved: '#2EC4A0', pending: '#E9C46A', rejected: '#E76F51', flagged: '#F97316' };

  return (
    <div style={{ marginLeft: depth > 0 ? '32px' : 0, borderLeft: depth > 0 ? '2px solid rgba(59,111,212,0.2)' : 'none', paddingLeft: depth > 0 ? '20px' : 0, marginBottom: '16px' }}>
      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', padding: '20px', position: 'relative' }}>
        {/* Admin status badge */}
        {isAdmin && (
          <div style={{ position: 'absolute', top: '12px', right: '12px', display: 'flex', gap: '6px', alignItems: 'center' }}>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.55rem', color: statusColors?.[comment?.status] || '#888', letterSpacing: '0.15em', textTransform: 'uppercase', border: `1px solid ${statusColors?.[comment?.status] || '#888'}33`, padding: '2px 6px' }}>{comment?.status?.toUpperCase()}</span>
          </div>
        )}
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
          <div style={{ width: '36px', height: '36px', background: '#3B6FD4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))' }}>
            <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '0.875rem', color: '#EEF0F2' }}>{comment?.author_name?.[0]?.toUpperCase() || '?'}</span>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '0.875rem', color: '#EEF0F2', margin: '0 0 2px' }}>{comment?.author_name}</p>
            <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: 'rgba(138,149,163,0.6)', letterSpacing: '0.08em', margin: 0 }}>{formatNorwegianDate(comment?.created_at)}</p>
          </div>
        </div>
        {/* Content */}
        <p style={{ fontFamily: 'Georgia, serif', fontSize: '0.9375rem', color: 'rgba(238,240,242,0.8)', lineHeight: 1.7, margin: '0 0 16px' }}>{comment?.content}</p>
        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <button onClick={handleLike} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'Syne, sans-serif', fontSize: '0.7rem', fontWeight: 700, color: liked ? '#3B6FD4' : 'rgba(238,240,242,0.4)', background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'color 0.2s' }}>
            <span style={{ fontSize: '1rem' }}>{liked ? '♥' : '♡'}</span> {likesCount > 0 ? likesCount : ''} LIKER
          </button>
          {depth < 2 && (
            <button onClick={() => setShowReply(!showReply)} style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.7rem', fontWeight: 700, color: 'rgba(238,240,242,0.4)', background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#3B6FD4'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(238,240,242,0.4)'}
            >↩ SVAR</button>
          )}
          {/* Admin moderation controls */}
          {isAdmin && (
            <>
              {comment?.status !== 'approved' && <button onClick={() => onModerate?.(comment?.id, 'approved')} style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.65rem', fontWeight: 700, color: '#2EC4A0', background: 'rgba(46,196,160,0.08)', border: '1px solid rgba(46,196,160,0.3)', padding: '4px 10px', cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase' }}>✓ GODKJENN</button>}
              {comment?.status !== 'rejected' && <button onClick={() => onModerate?.(comment?.id, 'rejected')} style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.65rem', fontWeight: 700, color: '#E76F51', background: 'rgba(231,111,81,0.08)', border: '1px solid rgba(231,111,81,0.3)', padding: '4px 10px', cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase' }}>✗ AVVIS</button>}
              {comment?.status !== 'flagged' && <button onClick={() => onModerate?.(comment?.id, 'flagged')} style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.65rem', fontWeight: 700, color: '#F97316', background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.3)', padding: '4px 10px', cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase' }}>⚑ FLAGG</button>}
              <button onClick={() => onModerate?.(comment?.id, 'delete')} style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.65rem', fontWeight: 700, color: '#E76F51', background: 'none', border: '1px solid rgba(231,111,81,0.2)', padding: '4px 10px', cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase' }}>🗑 SLETT</button>
            </>
          )}
        </div>
        {showReply && (
          <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '16px' }}>
            <CommentForm postId={comment?.post_id} parentId={comment?.id} onSuccess={() => { setShowReply(false); onReplySuccess?.(); }} onCancel={() => setShowReply(false)} />
          </div>
        )}
      </div>
      {/* Nested replies */}
      {comment?.replies?.map(reply => (
        <CommentItem key={reply?.id} comment={reply} depth={depth + 1} onReplySuccess={onReplySuccess} isAdmin={isAdmin} onModerate={onModerate} />
      ))}
    </div>
  );
};

const CommentThread = ({ postId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const isAdmin = user?.email?.includes('admin') || user?.user_metadata?.role === 'admin';

  const fetchComments = async () => {
    try {
      setLoading(true);
      const query = supabase?.from('comments')?.select('*')?.eq('post_id', postId)?.order('created_at', { ascending: true });
      const { data } = isAdmin ? await query : await query?.eq('status', 'approved');
      const commentList = data || [];
      // Build tree
      const roots = commentList?.filter(c => !c?.parent_id);
      const buildTree = (parent) => ({
        ...parent,
        replies: commentList?.filter(c => c?.parent_id === parent?.id)?.map(buildTree)
      });
      setComments(roots?.map(buildTree));
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { if (postId) fetchComments(); }, [postId, isAdmin]);

  const handleModerate = async (commentId, action) => {
    if (action === 'delete') {
      await supabase?.from('comments')?.delete()?.eq('id', commentId);
    } else {
      await supabase?.from('comments')?.update({ status: action })?.eq('id', commentId);
    }
    fetchComments();
  };

  const totalCount = comments?.length + comments?.reduce((acc, c) => acc + (c?.replies?.length || 0), 0);

  return (
    <div style={{ marginTop: '64px', borderTop: '2px solid rgba(255,255,255,0.08)', paddingTop: '48px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h2 style={{ fontFamily: 'Impact, sans-serif', fontSize: '2rem', color: '#EEF0F2', letterSpacing: '-0.02em', margin: 0, textTransform: 'uppercase' }}>KOMMENTARER</h2>
          {totalCount > 0 && <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem', color: '#3B6FD4', border: '1px solid rgba(59,111,212,0.4)', padding: '4px 10px', letterSpacing: '0.1em' }}>{totalCount}</span>}
        </div>
        <button onClick={() => setShowForm(!showForm)} style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.75rem', fontWeight: 700, color: '#050A12', background: '#3B6FD4', border: 'none', padding: '12px 24px', cursor: 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          {showForm ? 'AVBRYT' : '+ LEGG TIL KOMMENTAR'}
        </button>
      </div>
      {/* New comment form */}
      {showForm && (
        <div style={{ background: '#0D1520', border: '1px solid rgba(59,111,212,0.2)', padding: '24px', marginBottom: '32px' }}>
          <p style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.65rem', fontWeight: 700, color: '#3B6FD4', letterSpacing: '0.25em', textTransform: 'uppercase', margin: '0 0 16px' }}>NY KOMMENTAR</p>
          <CommentForm postId={postId} onSuccess={() => { setShowForm(false); fetchComments(); }} onCancel={() => setShowForm(false)} />
        </div>
      )}
      {/* Loading */}
      {loading && <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: 'rgba(238,240,242,0.3)', textAlign: 'center', padding: '32px 0' }}>Laster kommentarer...</p>}
      {/* Empty state */}
      {!loading && comments?.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0', border: '1px dashed rgba(255,255,255,0.08)' }}>
          <p style={{ fontFamily: 'Impact, sans-serif', fontSize: '1.5rem', color: 'rgba(255,255,255,0.06)', letterSpacing: '0.1em', margin: '0 0 8px' }}>INGEN KOMMENTARER</p>
          <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: 'rgba(238,240,242,0.3)', fontSize: '0.875rem' }}>Vær den første til å kommentere denne artikkelen.</p>
        </div>
      )}
      {/* Comment list */}
      {!loading && comments?.map(comment => (
        <CommentItem key={comment?.id} comment={comment} depth={0} onReplySuccess={fetchComments} isAdmin={isAdmin} onModerate={handleModerate} />
      ))}
    </div>
  );
};

export default CommentThread;
